import { useEffect, useRef, useState } from "react";
import { RiCloseLine } from "react-icons/ri";
import cities from "../POS/cities.json";
import countries from "../POS/countries.json";
import { CreateClientPRO } from "UpdatedFeatures/api";
import { toast } from "react-toastify";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Puff } from "react-loader-spinner";

const CreateClientPROModal = ({ open, setOpen }) => {
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedState, setSelectedState] = useState("");
  const [countrySearch, setCountrySearch] = useState("");
  const [stateSearch, setStateSearch] = useState("");
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);

  const countryRef = useRef(null);
  const stateRef = useRef(null);

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    company_name: "",
    country: "",
    city: "",
    adress: "",
    zip_code: "",
    registration_document: null,
  });

  // Filter countries based on search
  const filteredCountries = countries.filter((country) =>
    country.name_fr.toLowerCase().includes(countrySearch.toLowerCase())
  );

  // Get states based on selected country
  // const countryStates = cities.filter(
  //   (city) => city.stateId === String(selectedCountry)
  // );

  // Filter states based on search
  // const filteredStates = countryStates.filter((city) =>
  //   city.name_fr.toLowerCase().includes(stateSearch.toLowerCase())
  // );

  // Handle country selection
  const handleSelectCountry = (country) => {
    setSelectedCountry(country.id);
    setCountrySearch(country.name_fr);
    setFormData((prev) => ({ ...prev, country: country.name_fr }));
    setShowCountryDropdown(false);
    setSelectedState("");
    setStateSearch("");
  };

  // Handle state selection
  const handleSelectState = (state) => {
    setSelectedState(state.id);
    setStateSearch(state.name_fr);
    setFormData((prev) => ({ ...prev, city: state.name_fr }));
    setShowStateDropdown(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (countryRef.current && !countryRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
      if (stateRef.current && !stateRef.current.contains(event.target)) {
        setShowStateDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const queryClient = useQueryClient();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      registration_document: e.target.files[0],
    }));
  };

  const CreateClientPROMutation = useMutation({
    mutationFn: CreateClientPRO,
    onSuccess: (data) => {
      queryClient.invalidateQueries(["transfers"]);
      toast.success("Creation réussie !");
      console.log("Creation successful:", data);
      setOpen();
    },
    onError: (error) => {
      const errorMessage = error?.error?.[0];
      console.log(error);
      if (errorMessage === "EMAIL_UNIQUE") {
        toast.error("Cet Email existe déjà !");
      } else {
        toast.error("Une erreur est survenue. Veuillez réessayer.");
      }
    },
  });
  function convertUnicodeToEmoji(unicode) {
    return unicode
      .split(" ")
      .map((u) => String.fromCodePoint(parseInt(u.replace("U+", ""), 16)))
      .join("");
  }
  const handleSubmit = async (e) => {
    console.log("Submitting..");
    e.preventDefault();
    const requiredFields = [
      "first_name",
      "last_name",
      "email",
      "phone",
      "company_name",
      "country",
      "city",
      "adress",
      "zip_code",
    ];
    const emptyFields = requiredFields.filter(
      (field) => !formData[field].trim()
    );

    if (emptyFields.length > 0 || !formData.registration_document) {
      toast.error("Veuillez remplir tous les champs obligatoires !");
      return;
    }

    CreateClientPROMutation.mutate(formData);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50"
      style={{ zIndex: 9990, overflow: "visible" }}
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-gray-500 bg-opacity-75"
        aria-hidden="true"
        onClick={() => setOpen(false)}
      />

      {/* Modal Content */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div
          className="relative w-full max-w-3xl mx-auto bg-white shadow-lg rounded-lg overflow-auto max-h-[80vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex justify-between items-center p-6 bg-[#b269ae] border-b border-gray-500/30 pb-4">
            <h3
              id="modal-title"
              className="text-lg font-medium leading-6 text-white"
            >
              Créer un Client PRO
            </h3>
            <button
              type="button"
              className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-gray-500 hover:scale-105 duration-200"
              onClick={() => setOpen(false)}
            >
              <RiCloseLine className="h-6 w-6 text-white" aria-hidden="true" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 p-6">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2">
              {/* Personal Information */}
              <div>
                <label
                  htmlFor="first_name"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Nom du dirigeant
                </label>
                <input
                  onChange={handleChange}
                  value={formData.first_name}
                  type="text"
                  id="first_name"
                  name="first_name"
                  className="mt-2 block w-full rounded-lg border border-gray-500/30 px-4 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#b269ae] sm:text-sm sm:leading-6"
                />
              </div>

              <div>
                <label
                  htmlFor="last_name"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Prénom du dirigeant
                </label>
                <input
                  onChange={handleChange}
                  value={formData.last_name}
                  type="text"
                  id="last_name"
                  name="last_name"
                  className="mt-2 block w-full rounded-lg border border-gray-500/30 px-4 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#b269ae] sm:text-sm sm:leading-6"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Adresse mail du dirigeant
                </label>
                <input
                  onChange={handleChange}
                  value={formData.email}
                  type="email"
                  id="email"
                  name="email"
                  className="mt-2 block w-full rounded-lg border border-gray-500/30 px-4 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#b269ae] sm:text-sm sm:leading-6"
                />
              </div>

              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Téléphone du dirigeant
                </label>
                <input
                  onChange={handleChange}
                  value={formData.phone}
                  type="tel"
                  id="phone"
                  name="phone"
                  className="mt-2 block w-full rounded-lg border border-gray-500/30 px-4 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#b269ae] sm:text-sm sm:leading-6"
                />
              </div>
              <div className="sm:col-span-2">
                <label
                  htmlFor="company_name"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Raison sociale
                </label>
                <input
                  onChange={handleChange}
                  value={formData.company_name}
                  type="text"
                  id="company_name"
                  name="company_name"
                  className="mt-2 block w-full rounded-lg border border-gray-500/30 px-4 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#b269ae] sm:text-sm sm:leading-6"
                />
              </div>
              <div>
                <label
                  htmlFor="country"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Pays
                </label>
                <div className="relative" ref={countryRef}>
                  <input
                    type="text"
                    id="country"
                    value={countrySearch}
                    onChange={(e) => {
                      setCountrySearch(e.target.value);
                    }}
                    onClick={() => setShowCountryDropdown(true)}
                    placeholder="Rechercher un pays..."
                    className="mt-2 block w-full rounded-lg border border-gray-500/30 px-4 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#b269ae] sm:text-sm sm:leading-6"
                  />
                  {showCountryDropdown && (
                    <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                      {filteredCountries.map((country) => (
                        <li
                          key={country.id}
                          onClick={() => handleSelectCountry(country)}
                          className="px-3 py-2 cursor-pointer hover:bg-gray-200"
                        >
                          {convertUnicodeToEmoji(country.emojiU)}{" "}
                          {country.name_fr}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <div>
                <label
                  htmlFor="city"
                  className="block text-sm font-medium text-gray-700"
                >
                  Ville
                </label>
                <input
                  id="city"
                  name="city"
                  type="text"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="Ville"
                  className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm py-2 px-3 h-12"
                />
              </div>
              <div>
                <label
                  htmlFor="adress"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Adresse
                </label>
                <input
                  onChange={handleChange}
                  type="text"
                  id="adress"
                  name="adress"
                  className="mt-2 block w-full rounded-lg border border-gray-500/30 px-4 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#b269ae] sm:text-sm sm:leading-6"
                />
              </div>
              <div>
                <label
                  htmlFor="zip_code"
                  className="block text-sm font-medium leading-6 text-gray-900"
                >
                  Code Postal / ZIP
                </label>
                <input
                  onChange={handleChange}
                  type="text"
                  id="zip_code"
                  name="zip_code"
                  className="mt-2 block w-full rounded-lg border border-gray-500/30 px-4 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#b269ae] sm:text-sm sm:leading-6"
                />
              </div>
              <div className="flex flex-col items-start justify-center w-full gap-3 sm:col-span-2">
                <span className="block text-sm font-medium leading-6 text-gray-900">
                  Fournir un document d'immatriculation de la société:
                </span>
                <label
                  htmlFor="registration_document"
                  className="flex flex-col items-center justify-center w-full h-64 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                >
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg
                      className="w-8 h-8 mb-4 text-gray-500"
                      aria-hidden="true"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 20 16"
                    >
                      <path
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
                      />
                    </svg>
                    <p className="mb-2 text-sm text-gray-500">
                      <span className="font-semibold">
                        Cliquez pour télécharger
                      </span>{" "}
                      ou faites glisser et déposez
                    </p>
                    <p className="text-xs text-gray-500">
                      PDF, PNG ou JPG (MAX. 2MB)
                    </p>
                  </div>
                  <input
                    id="registration_document"
                    type="file"
                    name="registration_document"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
                {formData.registration_document && (
                  <p className="mt-2 text-sm text-gray-700">
                    Fichier sélectionné : {formData.registration_document.name}
                  </p>
                )}
              </div>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                onClick={handleSubmit}
                disabled={CreateClientPROMutation.isPending}
                className="inline-flex items-center gap-3 px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#b269ae] hover:bg-[#a058a0] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#b269ae] disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {CreateClientPROMutation.isPending ? (
                  <>
                    <Puff
                      color="white"
                      height={16}
                      width={16}
                      className="mr-2"
                    />
                    Création en cours...
                  </>
                ) : (
                  "Créer le Client PRO"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateClientPROModal;
